/**
 * La réclamation contentieuse en matière de cotisation foncière des entreprises.
 *
 * Retour à une lettre, après deux imprimés officiels : il n'existe aucun Cerfa
 * de réclamation, et le livre des procédures fiscales se contente d'en fixer la
 * forme. L'article R*197-3 exige quatre choses, et ce module les produit
 * toutes : l'imposition contestée est désignée, les moyens sont exposés, les
 * conclusions sont écrites, et la place de la signature manuscrite est laissée.
 * La quatrième pièce du dossier, l'avis d'imposition, ne peut venir que du
 * lecteur : la lettre la réclame et l'écran la répète.
 *
 * LE TRI EST LA MOITIÉ DE L'OUTIL. Beaucoup croient pouvoir se faire rembourser
 * une CFE qu'ils doivent, et repartent avec une réclamation rejetée qui leur a
 * coûté un après-midi. Quatre motifs seulement ouvrent droit à un dégrèvement
 * pour un micro-entrepreneur, chacun avec son article et sa pièce :
 *   - l'année de création, où aucune CFE n'est due (CGI, art. 1478-II) ;
 *   - l'avis reçu avant le premier euro encaissé, l'année de création au sens
 *     de la CFE étant celle du premier chiffre d'affaires ;
 *   - le chiffre d'affaires de la période de référence inférieur ou égal à
 *     5 000 €, qui exonère de la cotisation MINIMUM (CGI, art. 1647 D) ;
 *   - la cessation d'activité en cours d'année, qui ouvre une réduction au
 *     prorata (CGI, art. 1478-I).
 *
 * LE DÉLAI SE CALCULE, IL NE SE DEVINE PAS. L'article R*196-2 du livre des
 * procédures fiscales donne jusqu'au 31 décembre de l'année qui suit la mise en
 * recouvrement. Une réclamation déposée après est irrecevable, quel que soit le
 * bien-fondé du motif : autant le dire avant de faire remplir huit champs.
 */

import { dateEnToutesLettres, identite, nettoyer } from "./documentText.js";

/** Le seuil qui exonère de la cotisation minimum, CGI art. 1647 D. */
export const SEUIL_COTISATION_MINIMUM = 5000;

/**
 * Les motifs recevables, avec ce que chacun exige et ce qu'il obtient.
 *
 * `moyens` est la phrase de droit qui part dans la lettre, `conclusion` ce qui
 * est demandé, `pieces` ce qu'il faut joindre en plus de l'avis d'imposition.
 * Les textes sont cités par leur article, jamais par un commentaire.
 */
export const MOTIFS = [
    {
        cle: "anneeCreation",
        article: "le II de l'article 1478 du code général des impôts",
        obtient: "degrevementTotal",
    },
    {
        cle: "pasDeChiffreDAffaires",
        article: "le II de l'article 1478 du code général des impôts",
        obtient: "degrevementTotal",
    },
    {
        cle: "seuilCotisationMinimum",
        article: "l'article 1647 D du code général des impôts",
        obtient: "degrevementTotal",
    },
    {
        cle: "cessation",
        article: "le I de l'article 1478 du code général des impôts",
        obtient: "reductionProrata",
    },
];

export const motifParCle = (cle) => MOTIFS.find((m) => m.cle === cle) || null;

/**
 * La date limite de réclamation : le 31 décembre de l'année qui suit celle de
 * la mise en recouvrement (LPF, art. R*196-2).
 */
export function limiteReclamation(anneeImposition) {
    // Number("") vaut zéro, et zéro est un entier : sans ce contrôle de forme,
    // une année vide produisait une échéance au 31 décembre de l'an 1.
    if (!/^\d{4}$/.test(String(anneeImposition ?? "").trim())) return "";
    return `${Number(anneeImposition) + 1}-12-31`;
}

/** Le délai est-il encore ouvert au jour dit ? */
export function delaiOuvert(anneeImposition, aujourdhui) {
    const limite = limiteReclamation(anneeImposition);
    if (!limite || !aujourdhui) return { ouvert: null, limite };
    return { ouvert: new Date(aujourdhui) <= new Date(`${limite}T23:59:59Z`), limite };
}

/**
 * La période de référence de la cotisation minimum, et le chiffre d'affaires à
 * y comparer.
 *
 * La période est l'avant-dernière année (N-2), ou la dernière (N-1) quand
 * l'entreprise n'existait pas encore. Et lorsqu'elle ne couvre pas douze mois,
 * le chiffre d'affaires est « ramené ou porté à douze mois » : une activité
 * lancée en octobre avec 2 000 € encaissés pèse 8 000 € annualisés, au-dessus
 * du seuil. C'est le calcul qui décide, et l'oublier fait déposer une
 * réclamation vouée au rejet.
 */
export function periodeDeReference(anneeImposition, dateCreation) {
    const annee = Number(anneeImposition);
    if (!Number.isInteger(annee)) return null;
    const creation = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(dateCreation || ""));
    if (!creation) return null;

    const anneeCreation = Number(creation[1]);
    const reference = anneeCreation <= annee - 2 ? annee - 2 : annee - 1;
    if (reference < anneeCreation) return { annee: reference, jours: 0, complete: false };

    // Nombre de jours d'activité dans l'année de référence.
    const debut = anneeCreation === reference ? new Date(`${dateCreation}T00:00:00Z`) : new Date(Date.UTC(reference, 0, 1));
    const fin = new Date(Date.UTC(reference, 11, 31));
    const jours = Math.round((fin - debut) / 86400000) + 1;
    const total = (reference % 4 === 0 && reference % 100 !== 0) || reference % 400 === 0 ? 366 : 365;

    return { annee: reference, jours, total, complete: jours >= total };
}

/** Le chiffre d'affaires annualisé, arrondi à l'euro. */
export function chiffreDAffairesAnnualise(montant, periode) {
    const brut = Number(String(montant).replace(/\s/g, ""));
    if (!Number.isFinite(brut) || !periode || !periode.jours) return null;
    if (periode.complete) return Math.round(brut);
    return Math.round((brut * periode.total) / periode.jours);
}

/**
 * Qui peut réclamer, et qui perdrait son temps.
 *
 * Quatre sorties, dans l'ordre où le parcours les pose : le motif, le délai,
 * et pour le seuil des 5 000 €, le chiffre d'affaires annualisé qui dépasse.
 */
export function concerne(reponses, aujourdhui) {
    const r = reponses || {};
    if (r.motif === "autre") return { concerne: false, motif: "autre" };
    if (!r.motif) return { concerne: null, motif: null };

    if (r.anneeImposition) {
        const { ouvert } = delaiOuvert(r.anneeImposition, aujourdhui);
        if (ouvert === false) return { concerne: false, motif: "horsDelai" };
    }

    if (r.motif === "seuilCotisationMinimum" && r.chiffreDAffaires !== "" && r.dateCreation) {
        const periode = periodeDeReference(r.anneeImposition, r.dateCreation);
        const annualise = chiffreDAffairesAnnualise(r.chiffreDAffaires, periode);
        if (annualise !== null && annualise > SEUIL_COTISATION_MINIMUM) {
            return { concerne: false, motif: "seuilDepasse" };
        }
    }

    return { concerne: true, motif: null };
}

/** Ce qui manque, ce qui est refusé, et ce qui mérite un mot. */
export function valider(reponses, aujourdhui) {
    const r = reponses || {};
    const manques = [];
    const invalides = [];
    const alertes = [];

    if (!concerne(r, aujourdhui).concerne) {
        return { valide: false, manques: ["motif"], invalides, alertes };
    }

    const requis = [
        ["prenoms", r.prenoms],
        ["nom", r.nom],
        ["voie", r.voie],
        ["commune", r.commune],
        ["serviceImpots", r.serviceImpots],
        ["lieuDeSignature", r.lieuDeSignature],
    ];
    for (const [cle, valeur] of requis) {
        if (!nettoyer(valeur)) manques.push(cle);
    }

    const cp = nettoyer(r.codePostal);
    if (!cp) manques.push("codePostal");
    else if (!/^\d{5}$/.test(cp)) invalides.push({ champ: "codePostal", motif: "format" });

    const siret = nettoyer(r.siret).replace(/\s/g, "");
    if (!siret) manques.push("siret");
    else if (!/^\d{14}$/.test(siret)) invalides.push({ champ: "siret", motif: "siret" });

    const montant = nettoyer(r.montant).replace(/\s/g, "").replace(",", ".");
    if (!montant) manques.push("montant");
    else if (!/^\d{1,7}(\.\d{1,2})?$/.test(montant)) invalides.push({ champ: "montant", motif: "format" });

    for (const cle of ["dateCreation", "dateDeSignature"]) {
        const brut = nettoyer(r[cle]);
        if (!brut) manques.push(cle);
        else if (!dateEnToutesLettres(brut)) invalides.push({ champ: cle, motif: "format" });
    }

    // Chaque motif réclame la donnée qui fonde ses moyens.
    if (r.motif === "seuilCotisationMinimum") {
        if (nettoyer(r.chiffreDAffaires) === "") manques.push("chiffreDAffaires");
        else if (!/^\d{1,9}$/.test(nettoyer(r.chiffreDAffaires).replace(/\s/g, ""))) {
            invalides.push({ champ: "chiffreDAffaires", motif: "format" });
        }
    }
    if (r.motif === "cessation") {
        const brut = nettoyer(r.dateCessation);
        if (!brut) manques.push("dateCessation");
        else if (!dateEnToutesLettres(brut)) invalides.push({ champ: "dateCessation", motif: "format" });
        else if (Number(brut.slice(0, 4)) !== Number(r.anneeImposition)) {
            invalides.push({ champ: "dateCessation", motif: "horsAnnee" });
        }
    }
    /* Née l'année de l'imposition, l'entreprise n'a pas de période de
       référence : c'est l'exonération de l'année de création qu'il faut
       invoquer, et invoquer le seuil ferait rejeter la réclamation. */
    if (
        r.motif === "seuilCotisationMinimum" &&
        dateEnToutesLettres(r.dateCreation) &&
        Number(r.dateCreation.slice(0, 4)) >= Number(r.anneeImposition)
    ) {
        invalides.push({ champ: "motif", motif: "creationMemeAnnee" });
    }

    if (r.motif === "anneeCreation" && dateEnToutesLettres(r.dateCreation)) {
        if (Number(r.dateCreation.slice(0, 4)) !== Number(r.anneeImposition)) {
            invalides.push({ champ: "dateCreation", motif: "horsAnnee" });
        }
    }

    // Sans référence d'avis, la réclamation reste recevable : R*197-3 demande
    // que l'imposition soit désignée, et l'année plus le SIRET y suffisent.
    if (!nettoyer(r.referenceAvis)) alertes.push("sansReference");

    return {
        valide: manques.length === 0 && invalides.length === 0,
        manques: [...new Set(manques)],
        invalides,
        alertes,
    };
}

/** Le dernier jour du mois d'une date ISO, pour le prorata de cessation. */
export function finDuMois(iso) {
    const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(String(iso || ""));
    if (!m) return "";
    const dernier = new Date(Date.UTC(Number(m[1]), Number(m[2]), 0));
    return dernier.toISOString().slice(0, 10);
}

/**
 * Le même montant, mais AU MILIEU D'UNE PHRASE : « 247 euros ».
 *
 * Le symbole € n'a aucune largeur d'avance dans un PDF composé avec les polices
 * standard : le mot qui le suit se pose par-dessus, et la lettre imprimait
 * « pour un montant de 247 €dont l'avis porte la référence ». Voir
 * `eurosEnPhrase` dans documentText.js pour le détail du défaut. Le symbole ne
 * reste permis que là où il TERMINE une chaîne.
 */
export function montantEnPhrase(brut) {
    const avecSymbole = montantLisible(brut);
    return avecSymbole ? `${avecSymbole.replace(/\u00a0€$/, "")} euros` : "";
}

/** Le montant, écrit comme dans une lettre : « 247 € » ou « 247,50 € ». */
export function montantLisible(brut) {
    const n = Number(String(brut).replace(/\s/g, "").replace(",", "."));
    if (!Number.isFinite(n)) return "";
    const entier = Math.trunc(n);
    const centimes = Math.round((n - entier) * 100);
    /* Espace insécable entre les milliers et devant le symbole : un montant ne
       se coupe jamais en fin de ligne. toLocaleString rend une insécable
       ÉTROITE selon la version d'ICU, que la police standard d'un PDF ne sait
       pas dessiner : on la ramène à l'insécable ordinaire. */
    const espace = entier.toLocaleString("fr-FR").replace(/[\u202f\u00a0\s]/g, "\u00a0");
    return centimes
        ? `${espace},${String(centimes).padStart(2, "0")}\u00a0€`
        : `${espace}\u00a0€`;
}


/**
 * Les moyens de droit, selon le motif.
 *
 * Une phrase de fait, une phrase de droit : c'est ce que R*197-3 appelle
 * l'exposé sommaire des moyens, et une réclamation qui s'en tient là passe
 * mieux qu'une lettre qui plaide.
 */
function moyens(r) {
    const annee = Number(r.anneeImposition);
    switch (r.motif) {
        case "anneeCreation":
            return (
                `Mon entreprise a été créée le ${dateEnToutesLettres(r.dateCreation)}, ` +
                `soit au cours de l'année ${annee}. Or, en application du II de l'article 1478 ` +
                "du code général des impôts, la cotisation foncière des entreprises n'est pas due " +
                "au titre de l'année de la création de l'établissement."
            );
        case "pasDeChiffreDAffaires":
            return (
                `Mon entreprise, immatriculée le ${dateEnToutesLettres(r.dateCreation)}, n'avait ` +
                `réalisé aucun chiffre d'affaires au 31 décembre ${annee}. L'année de création à ` +
                "retenir pour la cotisation foncière des entreprises étant celle au cours de " +
                "laquelle l'entreprise réalise son premier chiffre d'affaires, l'exonération " +
                `prévue au II de l'article 1478 du code général des impôts s'applique à l'année ${annee}.`
            );
        case "seuilCotisationMinimum": {
            const periode = periodeDeReference(r.anneeImposition, r.dateCreation);
            const brut = Number(nettoyer(r.chiffreDAffaires).replace(/\s/g, ""));
            const annualise = chiffreDAffairesAnnualise(r.chiffreDAffaires, periode);
            const phraseProrata =
                periode && !periode.complete
                    ? ` Rapporté à douze mois, comme le prévoit ce même article lorsque la période ` +
                      `de référence n'en compte pas autant, ce montant s'établit à ${montantLisible(annualise)}.`
                    : "";
            return (
                `Le chiffre d'affaires réalisé au cours de la période de référence, soit ` +
                `l'année ${periode ? periode.annee : annee - 2}, s'élève à ${montantEnPhrase(brut)}.` +
                phraseProrata +
                " En application de l'article 1647 D du code général des impôts, les redevables " +
                "dont le montant du chiffre d'affaires ou des recettes est inférieur ou égal à " +
                "5 000 euros sont exonérés de la cotisation minimum."
            );
        }
        case "cessation":
            return (
                `J'ai cessé toute activité dans cet établissement le ${dateEnToutesLettres(r.dateCessation)}, ` +
                "sans céder ni transférer cette activité. En application du I de l'article 1478 du " +
                "code général des impôts, le contribuable qui cesse toute activité dans un " +
                "établissement n'est pas redevable de la cotisation foncière des entreprises pour " +
                "les mois restant à courir."
            );
        default:
            return "";
    }
}

/** Ce qui est demandé, selon ce que le motif permet d'obtenir. */
function conclusions(r) {
    const motif = motifParCle(r.motif);
    if (motif && motif.obtient === "reductionProrata") {
        return (
            "En conséquence, je vous demande de bien vouloir réduire cette imposition au prorata " +
            `de la période allant du 1er janvier ${r.anneeImposition} au ` +
            `${dateEnToutesLettres(finDuMois(r.dateCessation))}, et de me restituer le trop-versé.`
        );
    }
    return (
        "En conséquence, je vous demande de bien vouloir prononcer le dégrèvement de cette " +
        "imposition et de me restituer les sommes déjà versées."
    );
}

/**
 * Les pièces à joindre : l'avis d'imposition, exigé par R*197-3, puis celles
 * qui prouvent le motif.
 */
export function pieces(reponses) {
    const r = reponses || {};
    const communes = [
        "Une copie de votre avis de cotisation foncière des entreprises, exigée par l'article R*197-3 du livre des procédures fiscales.",
    ];
    switch (r.motif) {
        case "anneeCreation":
            return [...communes, "Votre avis de situation au répertoire Sirene, qui porte la date de création."];
        case "pasDeChiffreDAffaires":
            return [
                ...communes,
                "Votre avis de situation au répertoire Sirene, qui porte la date de création.",
                "Vos déclarations de chiffre d'affaires à l'Urssaf pour l'année contestée, qui montrent l'absence d'encaissement.",
            ];
        case "seuilCotisationMinimum":
            return [
                ...communes,
                "Vos déclarations de chiffre d'affaires à l'Urssaf pour la période de référence.",
            ];
        case "cessation":
            return [
                ...communes,
                "La notification de radiation, ou le récépissé de votre déclaration de cessation au guichet unique.",
                "Votre avis de situation au répertoire Sirene mentionnant la cessation.",
            ];
        default:
            return communes;
    }
}

/**
 * La lettre, en blocs, prête à être posée sur une page ou dans un PDF.
 *
 * Rend `null` tant qu'il manque une réponse : une réclamation à trous ne se
 * télécharge pas, et l'appelant n'a pas à décider ce qu'il fait d'un trou.
 */
export function composerReclamation(reponses, aujourdhui) {
    const r = reponses || {};
    const dateDeSignature = nettoyer(r.dateDeSignature) || String(aujourdhui).slice(0, 10);
    const complet = { ...r, dateDeSignature };
    if (!valider(complet, aujourdhui).valide) return null;

    const nomComplet = identite({ prenoms: r.prenoms, nom: r.nom });
    const reference = nettoyer(r.referenceAvis);

    return {
        titre: `Réclamation contentieuse relative à la cotisation foncière des entreprises ${r.anneeImposition}`,
        blocs: [
            /* LA DISPOSITION D'UNE LETTRE ADMINISTRATIVE FRANÇAISE : expéditeur
               à gauche, service destinataire à droite, lieu et date sous lui.
               Empilés à gauche, les trois blocs se lisaient comme une liste, et
               rien ne disait qui écrivait à qui. Pas d'intitulés : ce sont des
               colonnes de lettre, pas les cases d'un formulaire. */
            {
                type: "entete",
                colonnes: [
                    {
                        lignes: [
                            nomComplet,
                            nettoyer(r.voie),
                            `${nettoyer(r.codePostal)} ${nettoyer(r.commune)}`,
                            `SIRET : ${nettoyer(r.siret).replace(/\s/g, "")}`,
                        ],
                    },
                    {
                        lignes: [
                            `Service des impôts des entreprises de ${nettoyer(r.serviceImpots)}`,
                            "",
                            "",
                            `${nettoyer(r.lieuDeSignature)}, le ${dateEnToutesLettres(dateDeSignature)}`,
                        ],
                    },
                ],
            },
            {
                type: "lignes",
                lignes: [
                    `Objet : réclamation contentieuse, cotisation foncière des entreprises ${r.anneeImposition}`,
                    ...(reference ? [`Référence de l'avis : ${reference}`] : []),
                ],
            },
            { type: "paragraphe", texte: "Madame, Monsieur," },
            {
                type: "paragraphe",
                texte:
                    "Je conteste la cotisation foncière des entreprises mise à ma charge au titre " +
                    `de l'année ${r.anneeImposition}, pour un montant de ${montantEnPhrase(r.montant)}` +
                    `${reference ? `, dont l'avis porte la référence ${reference}` : ""}.`,
            },
            { type: "paragraphe", texte: moyens(r) },
            { type: "paragraphe", texte: conclusions(r) },
            {
                type: "paragraphe",
                texte:
                    "La présente réclamation est formée dans le délai prévu à l'article R*196-2 du " +
                    "livre des procédures fiscales. Une copie de l'avis d'imposition contesté y est " +
                    "jointe, conformément à l'article R*197-3 du même livre.",
            },
            {
                type: "paragraphe",
                texte: "Je vous prie d'agréer, Madame, Monsieur, l'expression de mes salutations distinguées.",
            },
            { type: "signature", nom: nomComplet },
        ],
    };
}

/** Le récapitulatif de ce que la réclamation demande, avant téléchargement. */
export function recapitulatif(reponses, aujourdhui) {
    const r = reponses || {};
    if (!composerReclamation(r, aujourdhui)) return null;
    const { limite } = delaiOuvert(r.anneeImposition, aujourdhui);
    const periode = periodeDeReference(r.anneeImposition, r.dateCreation);

    return [
        ["Imposition contestée", `Cotisation foncière des entreprises ${r.anneeImposition}`],
        ["Montant", montantLisible(r.montant)],
        ...(nettoyer(r.referenceAvis) ? [["Référence de l'avis", nettoyer(r.referenceAvis)]] : []),
        ["Destinataire", `Service des impôts des entreprises de ${nettoyer(r.serviceImpots)}`],
        ["Demande", motifParCle(r.motif)?.obtient === "reductionProrata"
            ? `Réduction au prorata, jusqu'au ${dateEnToutesLettres(finDuMois(r.dateCessation))}`
            : "Dégrèvement total et restitution"],
        ["Fondement", motifParCle(r.motif)?.article || ""],
        ...(r.motif === "seuilCotisationMinimum" && periode
            ? [["Période de référence", `année ${periode.annee}${periode.complete ? "" : ", annualisée"}`]]
            : []),
        ["Réclamation recevable jusqu'au", dateEnToutesLettres(limite)],
    ];
}
